import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface BocageDetailMobileProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 80px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 2rem;
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 0.75rem 0;
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
  
  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
`;

const MainImage = styled.div`
  width: 100%;
  height: 280px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ProductTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
`;

const ProductDescription = styled.p`
  font-size: 1rem;
  color: #475569;
  line-height: 1.5;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const TechnicalDatasheet = styled(motion.button)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  background: #dc2626;
  border: none;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:active {
    transform: scale(0.98);
  }
`;

const PDFIcon = styled.div`
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  font-weight: bold;
  font-size: 0.75rem;
`;

const PDFText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
`;

const PDFLabel = styled.span`
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
`;

const PDFSubtext = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  img {
    max-width: 100px;
    height: auto;
    object-fit: contain;
  }
`;

const TabsSection = styled.div`
  margin-top: 1.5rem;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 1.5rem;
  gap: 0.5rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$active ? '#dc2626' : '#64748b'};
  border-bottom: 2px solid ${props => props.$active ? '#dc2626' : 'transparent'};
  transition: all 0.3s ease;
  font-size: 0.95rem;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:active {
    color: #dc2626;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 200px;
`;

const DescriptionSection = styled.div`
  background: white;
  padding: 1.25rem;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  border-bottom: 2px solid #dc2626;
  padding-bottom: 0.4rem;
`;

const SpecsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SpecItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
`;

const SpecLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
`;

const SpecValue = styled.span`
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const ResourcesSection = styled.div`
  background: white;
  padding: 1.25rem;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const BocageDetailMobile: React.FC<BocageDetailMobileProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeTab, setActiveTab] = useState('description');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: "Catalogue", path: "/" },
    { label: "Postes préfabriqués", path: "/category/postes-prefabriques" },
    { label: "BOCAGE", path: "" }
  ];

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleDatasheetClick = () => {
    window.open('/images/catalog/postes/BOCAGE/Nexans_BOCAGE.pdf', '_blank');
  };

  return (
    <ProductDetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <MainImage>
          <img src='/images/catalog/postes/BOCAGE.jpg' alt="BOCAGE" />
        </MainImage>

        <ProductInfo>
          <ProductTitle>BOCAGE</ProductTitle>
          <ProductDescription>
            Poste préfabriqué de transformation MT/BT à béton armé ferraillé vibré
          </ProductDescription>

          <ActionButtons>
            <TechnicalDatasheet
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <PDFIcon>PDF</PDFIcon>
              <PDFText>
                <PDFLabel>Fiche technique</PDFLabel>
                <PDFSubtext>Télécharger le PDF</PDFSubtext>
              </PDFText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ActionButtons>
        </ProductInfo>

        <TabsSection ref={ref}>
          <TabsHeader>
            <TabButton 
              $active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </TabButton>
            <TabButton 
              $active={activeTab === 'ressources'} 
              onClick={() => setActiveTab('ressources')}
            >
              Ressources
            </TabButton>
          </TabsHeader>

          <TabContent>
            {activeTab === 'description' && (
              <DescriptionSection>
                <SectionTitle>Description</SectionTitle>
                
                <SpecsList>
                  <SpecItem>
                    <SpecLabel>Normes</SpecLabel>
                    <SpecValue>
                      <strong>PRODUCT:</strong> IEC 62271-202<br/><br/>
                      <strong>Internationales:</strong> Nexans specification<br/><br/>
                      <strong>CEI:</strong> 62 271 202
                    </SpecValue>
                  </SpecItem>
                  
                  <SpecItem>
                    <SpecLabel>Description</SpecLabel>
                    <SpecValue>
                      Postes préfabriqués de transformation MT/BT spécialement dédiés pour les postes de livraison, postes doubles et les activités EnR.<br/><br/>
                      Les postes Biosco sont des postes à couloir de manœuvre.
                    </SpecValue>
                  </SpecItem>
                  
                  <SpecItem>
                    <SpecLabel>Caractéristiques</SpecLabel>
                    <SpecValue>
                      Pour plus d'information télécharger la fiche technique
                    </SpecValue>
                  </SpecItem>
                </SpecsList>
              </DescriptionSection>
            )}

            {activeTab === 'ressources' && (
              <ResourcesSection>
                <SectionTitle>Ressources</SectionTitle>
                <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.9rem' }}>
                  Aucune ressource disponible pour le moment.
                </p>
              </ResourcesSection>
            )}
          </TabContent>
        </TabsSection>
      </Container>
      <Footer t={t} isRTL={isRTL} />
    </ProductDetailPage>
  );
};

export default BocageDetailMobile; 